import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Flex, Heading, Notification, Spinner, Text, TextField } from "@telia/teddy";
import FwaPackagesTable from "./FwaPackagesTable";
import { checkFwaCoverage, setFwaSelectedPackage } from "../../../state/actions/fwaActions";
import { API_URL_RETAILER_FWA_ADDRESS_SEARCH } from "../../../api";
import { retailerFetch } from "../../../retailerFetch";
import {
    getFwaCoverageError,
    getFwaCoverageStatus,
    getFwaPackages,
    getFwaSelectedPackageId,
} from "../../../state/selectors/fwaSelectors";

type AddressResult = {
    addressId: string;
    streetName: string;
    streetNumber: string;
    streetLetter: string;
    postalCode: string;
    postalName: string;
    matrikkelnummer: string;
};

const TradlosAddressSearch: React.FC = () => {
    const dispatch = useDispatch();
    const coverageStatus = useSelector(getFwaCoverageStatus);
    const coverageError = useSelector(getFwaCoverageError);
    const coveragePackages = useSelector(getFwaPackages);
    const selectedPackageId = useSelector(getFwaSelectedPackageId);

    const [addressQuery, setAddressQuery] = useState("");
    const [addressResults, setAddressResults] = useState<AddressResult[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<AddressResult | undefined>(undefined);
    const [addressError, setAddressError] = useState<string | undefined>(undefined);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        [],
    );

    const handleCheckCoverage = useCallback(
        (addr: AddressResult) => {
            dispatch(checkFwaCoverage(addr.addressId));
        },
        [dispatch],
    );

    const searchAddresses = useCallback((query: string) => {
        if (query.length <= 4) {
            setAddressResults([]);
            setAddressError(undefined);
            return;
        }
        retailerFetch(API_URL_RETAILER_FWA_ADDRESS_SEARCH(query))
            .then((res) => {
                if (!res.ok) throw new Error(`Adressesøk feilet (${res.status} ${res.statusText})`);
                return res.json();
            })
            .then((json) => {
                if (json.error) throw new Error(json.error);
                if (!json.data || json.data.length === 0) {
                    setAddressResults([]);
                    setAddressError("Ingen adresser funnet. Sjekk at adressen er skrevet riktig.");
                    return;
                }
                setAddressResults(json.data.slice(0, 5));
                setAddressError(undefined);
            })
            .catch((err: Error) => {
                setAddressResults([]);
                setAddressError(err.message || "Kunne ikke hente adresser. Prøv igjen senere.");
            });
    }, []);

    return (
        <>
            <TextField>
                <TextField.Label>Gateadresse</TextField.Label>
                <TextField.Input
                    value={addressQuery}
                    onChange={(e) => {
                        const val = e.target.value;
                        setAddressQuery(val);
                        setSelectedAddress(undefined);
                        if (debounceRef.current) {
                            clearTimeout(debounceRef.current);
                        }
                        debounceRef.current = setTimeout(() => {
                            searchAddresses(val);
                        }, 300);
                    }}
                    placeholder="Skriv inn gateadresse..."
                    autoComplete="off"
                />
            </TextField>
            {addressError && (
                <Notification type="tile" variant="error" mt="100">
                    <Notification.Icon />
                    <Notification.Text>{addressError}</Notification.Text>
                </Notification>
            )}
            {!selectedAddress && addressResults.length > 0 && (
                <Box mt="100">
                    {addressResults.map((addr) => {
                        const selectAddr = () => {
                            setSelectedAddress(addr);
                            setAddressQuery(
                                `${addr.streetName} ${addr.streetNumber}${addr.streetLetter}, ${addr.postalCode} ${addr.postalName}`,
                            );
                            setAddressResults([]);
                            handleCheckCoverage(addr);
                        };
                        return (
                            <Box
                                key={addr.addressId}
                                className="choose-subscription__address-item"
                                role="button"
                                tabIndex={0}
                                onClick={selectAddr}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        selectAddr();
                                    }
                                }}
                            >
                                <Text>
                                    {addr.streetName} {addr.streetNumber}
                                    {addr.streetLetter}, {addr.postalCode} {addr.postalName}
                                    {addr.matrikkelnummer && <> &mdash; {addr.matrikkelnummer}</>}
                                </Text>
                            </Box>
                        );
                    })}
                </Box>
            )}
            {coverageStatus === "loading" && (
                <Flex mt="200" gap="200" align="center">
                    <Spinner size="sm" />
                    <Text>
                        Sjekker mulighetene for Trådløst Bredbånd på valgt adresse - dette kan ta opptil 1 minutt.
                    </Text>
                </Flex>
            )}
            {coverageStatus === "no_coverage" && (
                <Notification type="tile" variant="warning" mt="200">
                    <Notification.Icon />
                    <Notification.Text>
                        Trådløst Bredbånd er dessverre ikke tilgjengelig på denne adressen.
                    </Notification.Text>
                </Notification>
            )}
            {coverageStatus === "error" && (
                <Notification type="tile" variant="error" mt="200">
                    <Notification.Icon />
                    <Notification.Text>{coverageError ?? "Kunne ikke sjekke dekning. Prøv igjen."}</Notification.Text>
                </Notification>
            )}
            {coverageStatus === "covered" && (
                <Box mt="300">
                    {coveragePackages.length === 0 ? (
                        <Notification type="tile" variant="success">
                            <Notification.Icon />
                            <Notification.Text>
                                Trådløst Bredbånd er tilgjengelig på denne adressen, men ingen pakker er tilgjengelige
                                for øyeblikket.
                            </Notification.Text>
                        </Notification>
                    ) : (
                        <>
                            <Heading as="h3" variant="title-200" mb="200">
                                Tilgjengelige pakker
                            </Heading>
                            <FwaPackagesTable
                                packages={coveragePackages}
                                selectedPackageId={selectedPackageId}
                                onSelectPackage={(id) => dispatch(setFwaSelectedPackage(id))}
                            />
                        </>
                    )}
                </Box>
            )}
        </>
    );
};

export default TradlosAddressSearch;
